import React from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  HStack,
  Rating,
  theme,
  TitleText,
  useBreakpointHelpers,
  usePressableState,
} from '@sweetgreen/garnish';

import { PageWrapper } from '../../components/PageWrapper';

export const RatingContent = React.memo((props: RatingContentProps) => {
  const {
    ratingTitle,
    ratingSubtitle,
    ratingHint,
    ratingCta,
    cards,
    rating,
    submitting,
    onRating,
    onHoverRating,
    onSelectCard,
    onSubmit,
  } = props;
  const { isTablet, isMobile } = useBreakpointHelpers();

  return (
    <PageWrapper safeAreaEdges="all" withoutFooter withHeaderTitle>
      <View
        style={[
          styles.content,
          isTablet && styles.tabletPadding,
          isMobile && styles.mobilePadding,
        ]}
      >
        <Rating value={rating} onChange={onRating} onHover={onHoverRating} />
        <BodyText style={styles.ratingTitle}>{ratingTitle}</BodyText>
        <TitleText size={5} style={styles.ratingSubtitle}>
          {ratingSubtitle}
        </TitleText>
        <BodyText size={5} style={styles.ratingHint}>
          {ratingHint}
        </BodyText>
        <ScrollView contentContainerStyle={styles.scroll}>
          <HStack
            itemsPerRow={isMobile ? 2 : 3}
            customSpacing={theme.spacing.medium}
          >
            {cards.map((card) => (
              <RatingCardComponent
                key={card.id}
                {...card}
                onSelectCard={onSelectCard}
              />
            ))}
          </HStack>
        </ScrollView>
        <Button
          testID="rate-order.submit"
          width={320}
          accessibilityRole="button"
          accessibilityLabel={ratingCta}
          style={styles.callToAction}
          isLoading={submitting}
          onPress={onSubmit}
        >
          {ratingCta}
        </Button>
      </View>
    </PageWrapper>
  );
});

const RatingCardComponent = ({
  id,
  label,
  selected,
  onSelectCard,
}: RatingCardProps) => {
  const ref = React.useRef(null);
  const { isInteracting } = usePressableState(ref);
  const handleOnPress = React.useCallback(() => {
    onSelectCard(id, !selected);
  }, [id, selected, onSelectCard]);

  return (
    <Pressable
      ref={ref}
      testID={`rate-order.card.${id}`}
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={handleOnPress}
      style={[
        styles.card,
        selected ? styles.cardSelected : isInteracting && styles.cardHovered,
      ]}
    >
      <BodyText size={4} style={styles.cardLabel}>
        {label}
      </BodyText>
    </Pressable>
  );
};

type RatingContentProps = {
  readonly ratingTitle: string;
  readonly ratingSubtitle: string;
  readonly ratingHint: string;
  readonly ratingCta: string;
  readonly cards: readonly RatingCard[];
  readonly rating: number;
  readonly submitting: boolean;
  readonly onHoverRating: (rating: number) => void;
  readonly onRating: (rating: number) => void;
  readonly onSelectCard: (id: string, selected: boolean) => void;
  readonly onSubmit: () => void;
};

type RatingCardProps = {
  readonly id: string;
  readonly label: string;
  readonly selected: boolean;
  readonly onSelectCard: (id: string, selected: boolean) => void;
};

type RatingCard = {
  readonly id: string;
  readonly label: string;
  readonly selected: boolean;
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.xlarge,
    paddingBottom: theme.spacing.large + theme.spacing.small,
  },
  tabletPadding: {
    paddingHorizontal: theme.spacing.large,
    paddingBottom: theme.spacing.xlarge,
  },
  mobilePadding: {
    paddingHorizontal: theme.spacing.medium,
    paddingBottom: theme.spacing.large + theme.spacing.small,
  },
  scroll: {
    maxHeight: 300,
  },
  ratingTitle: {
    marginTop: theme.spacing.large,
    color: theme.colors.KALE,
  },
  ratingSubtitle: {
    marginTop: theme.spacing.large,
  },
  ratingHint: {
    color: theme.colors.CHARCOAL,
    marginTop: theme.spacing.large,
    marginBottom: theme.spacing.medium,
  },
  card: {
    minHeight: 64,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.OPACITY.GREEN_1.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.xxlarge,
    paddingVertical: theme.spacing.small,
    paddingHorizontal: theme.spacing.medium,
  },
  cardLabel: {
    textAlign: 'center',
  },
  cardHovered: {
    backgroundColor: theme.colors.OPACITY.GREEN_2.LIGHTEST,
  },
  cardSelected: {
    backgroundColor: theme.colors.OPACITY.GREEN_3.LIGHT,
    borderWidth: 1,
    borderColor: theme.colors.GREEN_2,
  },
  callToAction: {
    marginTop: theme.spacing.large,
  },
});
